import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

const StaffsMultiSelect = () => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: staffs,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: '/dictionary/staffes',
    queryFn: async function () {
      const response = await axiosPrivate.get('/dictionary/staffes');
      return response.data.data;
    },
    enabled: !hasError,
    onError: () => {
      setHasError(true);
    },
    retry: false,
  });
  function uniqueElements(arr) {
    return arr.filter((num) => arr.indexOf(num) === arr.lastIndexOf(num));
  }
  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    clearAll();
    const data = uniqueElements(value);
    data?.forEach((v, i) => {
      searchParams.set(`staffs[${i}]`, v);
    });
    setSearchParams(searchParams);
  };

  const handleDeleteItem = (value) => {
    let leftSelectedBlocks = selectedBlocks.filter((item) => item != value);
    clearAll();
    leftSelectedBlocks.forEach((item, index) =>
      searchParams.set(`staffs[${index}]`, item)
    );
    setSearchParams(searchParams);
  };

  const clearAll = () => {
    let entries = Object.fromEntries(searchParams);
    Object.keys(entries).forEach((item) => {
      if (item.includes('staffs[')) searchParams.delete(item);
    });
  };

  useEffect(() => {
    if (staffs) {
      const entries = Object.fromEntries(searchParams);
      const selected = [];
      Object.entries(entries).forEach(([key, val]) => {
        if (key.includes('staffs[')) selected.push(val);
      });
      setSelectedBlocks(selected);
    }
  }, [staffs, searchParams]);

  const handleClearSelectBox = () => {
    clearAll();
    setSelectedBlocks([]);
    setSearchParams(searchParams);
  };

  const getChipNameById = useCallback(
    (id) => {
      let found =
        staffs && staffs?.length > 0 && staffs?.find((item) => item?.id == id);
      if (found) {
        return found?.name;
      }
      return '';
    },
    [staffs]
  );

  return (
    <FormControl fullWidth color='formColor'>
      <InputLabel id='blocks-filter-label'>
        {t('common.fields.staffs')}
      </InputLabel>
      <Select
        labelId='blocks-filter-label'
        id='blocks-filter-multiple-chip'
        multiple
        value={selectedBlocks}
        onChange={handleChange}
        input={
          <OutlinedInput
            id='blocks-filter-select-multiple-chip'
            variant='filterField'
            label={t('common.filter.blocks')}
          />
        }
        renderValue={(selected) => (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              overflow: 'hidden',
              gap: 0.5,
            }}
          >
            {selected.map((value) => (
              <Chip
                key={`block-${value}`}
                label={getChipNameById(value)}
                variant='tableBadge'
                color='primary'
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                onDelete={() => handleDeleteItem(value)}
              />
            ))}
          </Box>
        )}
        color='formColor'
        variant='outlined'
        MenuProps={{
          id: 'blocks-filter-select-menu',
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
          disableScrollLock: true,
        }}
        endAdornment={
          <InputAdornment position='end' className='custom-endAdornment'>
            <IconButton
              onClick={() => handleClearSelectBox()}
              size='small'
              variant='onlyIcon'
              sx={{
                display: selectedBlocks.length > 0 ? '' : 'none',
              }}
              className='select-box-end-adornment-button'
            >
              <i className='bi bi-x end-adornment-close-button' />
            </IconButton>
          </InputAdornment>
        }
      >
        {isLoading || isFetching ? (
          <div className='circular-progress-box'>
            <CircularProgress size={25} />
          </div>
        ) : staffs && staffs.length > 0 ? (
          staffs.map((item, index) => (
            <MenuItem value={item?.id?.toString()} key={`block-${index + 1}`}>
              {item?.name}
            </MenuItem>
          ))
        ) : (
          <div>
            <span className='no-data-found-wrapper select-box'>
              <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
              {t('common.global.noDataFound')}
            </span>
          </div>
        )}
      </Select>
    </FormControl>
  );
};

export default memo(StaffsMultiSelect);
